import AppSettings from '../../app/AppSettings';
import ConfiguratorPageQueryParser from '../../app/configurator/ConfiguratorPageQueryParser';
import { Tab } from '../../app/configurator/Tab';
import { IApiClient } from '../../app/data/client';
import {
  GetBasicProductByIdQuery,
  GetConfigurationByCodeQuery,
  ProductConfigurationOrigin,
} from '../../app/data/model';
import { ProductFamilyQueryParser } from '../../app/products/ProductFamilyPageQuery';
import RouteDefinitions from '../../app/RouteDefinitions';
import { BasePageState } from '../../app/shared/BasePageState';
import { INavigationService } from '../../app/shared/NavigationService';
import { StoreUrlBuilder } from '../../app/shared/StoreUrlBuilder';
import { AbsoluteUrl, RelativeUrl } from '../../app/shared/Url';
import { CODE, SKU } from './ProductLookupConsts';
import ProductLookupQueryParser from './ProductLookupQueryParser';

export class LookupDestinationUrlBuilder {
  private client: IApiClient;

  constructor(client: IApiClient) {
    this.client = client;
  }

  public async buildFromSku(sku: string): Promise<RelativeUrl> {
    const response = await this.client.send(
      new GetBasicProductByIdQuery({
        productId: sku,
        shoppingContext: StoreUrlBuilder.context,
      }),
    );
    const familyId = response.product.familyId;
    const path = LookupDestinationUrlBuilder.productPageUrl(familyId);

    const query = ProductFamilyQueryParser.toQuery({
      model: sku,
    });

    return LookupDestinationUrlBuilder.buildGlobalStoreUrl(path, query);
  }

  public async buildFromCode(code: string, step: Tab = null): Promise<RelativeUrl> {
    const response = await this.client.send(new GetConfigurationByCodeQuery({ code: code }));
    const configuration = response.productConfiguration;

    if (configuration.origin === ProductConfigurationOrigin.DefaultConfigurator) {
      const path = LookupDestinationUrlBuilder.configuratorUrl(configuration.baseModelCode.toLocaleLowerCase());

      const query = ConfiguratorPageQueryParser.toQuery({
        options: configuration.options.map((x) => x.id),
        accessories: configuration.accessories.map((x) => x.id),
        step: step,
        quantity: 1,
        code: code,
      });

      return LookupDestinationUrlBuilder.buildGlobalStoreUrl(path, query);
    } else {
      const path = LookupDestinationUrlBuilder.configurationDetailsUrl(code);
      return LookupDestinationUrlBuilder.buildGlobalStoreUrl(path);
    }
  }

  private static productPageUrl(familyName: string) {
    return RouteDefinitions.getUrlTo((x) => x.productPageRoute, { slug: familyName });
  }

  private static configuratorUrl(slug: string) {
    return RouteDefinitions.getUrlTo((x) => x.configuratorProductRoute, { slug: slug.toLocaleLowerCase() });
  }

  private static configurationDetailsUrl(code: string) {
    return RouteDefinitions.getUrlTo((x) => x.configurationDetailsRoute, { code: code.toUpperCase() });
  }

  private static buildGlobalStoreUrl(path: string, query: Map<string, string> = new Map<string, string>()) {
    query.set('store', 'preserve');
    return StoreUrlBuilder.build('global', 'en-gb', path, query);
  }
}

export default class ProductLookupRedirectState extends BasePageState<null> {
  private setting: AppSettings;
  private readonly client: IApiClient;
  private navigation: INavigationService;

  constructor(setting: AppSettings, client: IApiClient, navigation: INavigationService) {
    super();
    this.setting = setting;
    this.client = client;
    this.navigation = navigation;
  }

  async onLoad(): Promise<void> {
    const model = ProductLookupQueryParser.toModel(this.navigation.currentUrl.query);

    if (!model.code && !model.sku) {
      throw new Error(`At least one of these query parameters must be provided: ${SKU}, ${CODE}`);
    }

    const builder = new LookupDestinationUrlBuilder(this.client);
    let destination;

    if (model.code) {
      destination = await builder.buildFromCode(model.code, Tab.Summary);
    } else {
      destination = await builder.buildFromSku(model.sku);
    }

    this.redirect(destination);
  }

  private redirect(destination: RelativeUrl) {
    const absoluteUrl = AbsoluteUrl.parse(this.setting.baseUrl).append(destination).toString();
    this.navigation.redirect(absoluteUrl);
  }
}
